/**
 * useAnchoredPanel — position a dropdown panel against its trigger, in a
 * portal on <body>.
 *
 * Why not `absolute` + a big z-index: every admin server card is a
 * `.glass-card`, and `backdrop-filter` creates a NEW STACKING CONTEXT. A
 * z-index inside card A can therefore never outrank card B, because they
 * compete as whole cards, not as descendants. The open pal list was being
 * painted under the next card and under the "No active raids" strip — the
 * admin could only read two rows. Portalling to <body> with `position:fixed`
 * removes the panel from every ancestor's stacking context and clip region.
 *
 * Shared by PalPicker and PassivePicker so there is one implementation of the
 * positioning rules, not two.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const PANEL_W = 280;
const GAP = 4;
const EDGE = 8;

export function useAnchoredPanel(open, onClose) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [style, setStyle] = useState(null);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const width = Math.min(PANEL_W, vw - EDGE * 2);
    const below = vh - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    // Open downward unless there is meaningfully more room above.
    const flip = below < 220 && above > below;
    setStyle({
      position: "fixed",
      width,
      left: Math.max(EDGE, Math.min(r.left, vw - width - EDGE)),
      top: flip ? undefined : r.bottom + GAP,
      bottom: flip ? vh - r.top + GAP : undefined,
      maxHeight: Math.max(180, flip ? above : below),
      zIndex: 9999,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) { setStyle(null); return undefined; }
    place();
    // Any scroll in any ancestor moves the trigger, so listen in capture.
    const onMove = () => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    // The panel is no longer a DOM descendant of the trigger, so the
    // outside-click test has to check both nodes.
    const onDocClick = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, onClose]);

  return { triggerRef, panelRef, style };
}
